import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, getDocs,
  doc, getDoc, deleteDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import Logo from '../components/Logo';

// 3C: claim/release go through callable functions (claimDevice,
// releaseDevice). The old direct-write path is gone — rules now
// deny client create on both the device doc and the claim doc.

export default function Devices({ user }) {
  const [devices, setDevices]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showAdd, setShowAdd]           = useState(false);
  const [deviceId, setDeviceId]         = useState('');
  const [claimToken, setClaimToken]     = useState('');
  const [locationName, setLocationName] = useState('');
  const [error, setError]               = useState('');
  const [adding, setAdding]             = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null);  // device pending removal
  const [confirmBusy, setConfirmBusy]     = useState(false);

  // ── Load the user's claimed devices ───────────────────────
  // Under the v8 schema: claim records live in users/{uid}/devices/
  // subcollection. Device-level metadata (name, tank1.name, tank2.name)
  // lives on devices/{deviceId}. Loading needs both reads per device.
  const loadDevices = useCallback(async () => {
    try {
      const claimsSnap = await getDocs(
        collection(db, 'users', user.uid, 'devices')
      );

      // For each claim, fetch the matching device doc to get the
      // user-facing names. If the device doc doesn't exist yet
      // (claim made but device never connected), fall back to the
      // claim's seeded values.
      const list = await Promise.all(claimsSnap.docs.map(async claimDoc => {
        const claim = { id: claimDoc.id, ...claimDoc.data() };
        try {
          const devSnap = await getDoc(doc(db, 'devices', claim.deviceId));
          const dev = devSnap.exists() ? devSnap.data() : {};
          return {
            ...claim,
            name:      dev.name           || claim.deviceId,
            tank1Name: dev.tank1?.name    || 'Tank 1',
            tank2Name: dev.tank2?.name    || 'Tank 2',
          };
        } catch (e) {
          // Device doc read failed (permissions, deleted, etc.) —
          // show what we can from the claim alone.
          console.warn(`Couldn't read devices/${claim.deviceId}:`, e);
          return {
            ...claim,
            name:      claim.deviceId,
            tank1Name: 'Tank 1',
            tank2Name: 'Tank 2',
          };
        }
      }));

      setDevices(list);
    } catch (err) {
      console.error('Error loading devices:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadDevices(); }, [loadDevices]);

  // ── Add (claim) a device ──────────────────────────────────
  // 3C: this calls the claimDevice cloud function rather than
  // writing claim/device docs directly. The cloud function does
  // the token check and the atomic ownership write.
  async function handleAdd(e) {
    e.preventDefault();
    setError('');
    const id    = deviceId.trim().toUpperCase();
    const token = claimToken.replace(/[-\s]/g, '').toUpperCase();
    const loc   = locationName.trim();

    if (!id) { setError('Please enter a device ID.'); return; }
    if (!token) { setError('Please enter the claim code.'); return; }
    if (!loc) { setError('Please enter a location name.'); return; }

    if (devices.find(d => d.deviceId === id)) {
      setError('You have already added this device.');
      return;
    }

    setAdding(true);
    try {
      const claimDevice = httpsCallable(functions, 'claimDevice');
      await claimDevice({
        deviceId:     id,
        claimToken:   token,
        locationName: loc,
      });

      setDeviceId('');
      setClaimToken('');
      setLocationName('');
      setShowAdd(false);
      await loadDevices();
    } catch (err) {
      console.error('Error adding device:', err);
      const code = err?.code || '';
      const msg  = err?.message || '';
      if (code === 'functions/not-found' || code === 'not-found') {
        setError("We don't recognize that device ID. Check the code on your unit's label or the card from the box.");
      } else if (code === 'functions/permission-denied' || code === 'permission-denied') {
        setError("Claim code doesn't match. Check the card from your box.");
      } else if (code === 'functions/failed-precondition' || code === 'failed-precondition') {
        setError(msg || 'This device cannot be claimed right now. Try again in a minute.');
      } else if (code === 'functions/invalid-argument' || code === 'invalid-argument') {
        setError(msg || 'Please check the device ID, claim code, and location name.');
      } else {
        setError('Could not add device. Please try again.');
      }
    } finally {
      setAdding(false);
    }
  }

  // ── Remove a device claim ─────────────────────────────────
  // Branches on role:
  //   - 'owner': call releaseDevice (deletes ALL claim docs for this
  //              device and clears ownership on the device doc).
  //   - 'shared': delete just this user's own claim doc (rules allow
  //              self-delete when role == 'shared').
  // Owners' direct delete is denied by rules in 3C — they must go
  // through the callable so the device doc is cleaned up too.
  // Removal opens a styled in-app confirmation (no native OS dialog), matching
  // the modal on the Settings page. The button just sets confirmTarget; this
  // runs on confirm.
  async function confirmRemove() {
    if (!confirmTarget) return;
    const { id: claimDocId, role } = confirmTarget;
    setConfirmBusy(true);
    setError('');
    try {
      if (role === 'owner') {
        const releaseDevice = httpsCallable(functions, 'releaseDevice');
        await releaseDevice({ deviceId: claimDocId });
      } else {
        await deleteDoc(doc(db, 'users', user.uid, 'devices', claimDocId));
      }
      setConfirmTarget(null);
      await loadDevices();
    } catch (err) {
      console.error('Error removing device:', err);
      setError('Could not remove device. Please try again.');
    } finally {
      setConfirmBusy(false);
    }
  }

  return (
    <div className="app-container">
      <TopBar subtitle="My devices" />

      <div className="page-content">

        {/* Add device button */}
        {!showAdd && (
          <button
            type="button"
            className="add-device-cta"
            onClick={() => setShowAdd(true)}
          >
            <div className="add-device-cta__left">
              <div className="add-device-cta__plus">+</div>
              <div className="add-device-cta__text">
                <div className="add-device-cta__title">Add a device</div>
                <div className="add-device-cta__sub">
                  Enter the device ID from your unit's label
                </div>
              </div>
            </div>
            <div className="add-device-cta__chevron">›</div>
          </button>
        )}

        {/* Add device form */}
        {showAdd && (
          <div className="card add-device-form">
            <div className="add-device-form__header">
              <h3>Add a device</h3>
              <button
                type="button"
                className="add-device-form__close"
                onClick={() => { setShowAdd(false); setError(''); }}
                aria-label="Cancel"
              >
                ×
              </button>
            </div>

            {error && (
              <div className="alert-banner fault">
                <div className="alert-banner-desc">{error}</div>
              </div>
            )}

            <form onSubmit={handleAdd}>
              <div className="field">
                <label>Device ID</label>
                <input
                  type="text"
                  placeholder="e.g. WT-0042"
                  value={deviceId}
                  onChange={e => setDeviceId(e.target.value)}
                  autoCapitalize="characters"
                  required
                />
                <div className="hint">
                  Found on the quick-start card from your box, or on the device label.
                </div>
              </div>

              <div className="field">
                <label>Claim code</label>
                <input
                  type="text"
                  placeholder="e.g. K7M3-PQR9-X2N"
                  value={claimToken}
                  onChange={e => setClaimToken(e.target.value)}
                  autoCapitalize="characters"
                  required
                />
                <div className="hint">
                  Printed below the QR code on the card from your box.
                </div>
              </div>

              <div className="field">
                <label>Location name</label>
                <input
                  type="text"
                  placeholder="Home"
                  value={locationName}
                  onChange={e => setLocationName(e.target.value)}
                  maxLength={32}
                  required
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={adding}
              >
                {adding ? 'Connecting…' : 'Connect device'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => { setShowAdd(false); setError(''); }}
              >
                Cancel
              </button>
            </form>
          </div>
        )}

        {/* Loading */}
        {loading && <div className="spinner" />}

        {/* Empty state */}
        {!loading && devices.length === 0 && !showAdd && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <Logo variant="icon" width={56} />
            </div>
            <h3>No devices yet</h3>
            <p>Connect your SoKo Water Systems device to start monitoring your water levels.</p>
            <button
              type="button"
              className="btn btn-primary"
              style={{ width: 'auto', padding: '10px 24px' }}
              onClick={() => setShowAdd(true)}
            >
              + Add your first device
            </button>
            <p className="empty-state-note">
              You'll need the device ID from the label on your unit.
            </p>
          </div>
        )}

        {/* Device list */}
        {!loading && devices.length > 0 && (
          <>
            <div className="device-count">
              {devices.length} device{devices.length !== 1 ? 's' : ''}
            </div>

            {devices.map(device => (
              <div key={device.id} className="card device-card">
                <div className="device-card__header">
                  <div className="device-card__name">{device.name}</div>
                  <button
                    type="button"
                    className="device-card__remove"
                    onClick={() => setConfirmTarget(device)}
                  >
                    − Remove
                  </button>
                </div>
                <div className="device-card__meta">ID: {device.deviceId}</div>
                <div className="device-card__meta">
                  {device.tank1Name} · {device.tank2Name}
                </div>
              </div>
            ))}
          </>
        )}

      </div>

      {/* Remove-device confirmation — mirrors the Settings modal (no native OS dialog). */}
      {confirmTarget && (
        <div
          className="modal-backdrop"
          onClick={() => { if (!confirmBusy) setConfirmTarget(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }}
        >
          <div className="card" onClick={e => e.stopPropagation()} style={{ maxWidth: 440, width: '100%', margin: 0 }}>
            <h3 style={{ marginTop: 0 }}>Remove {confirmTarget.name}?</h3>
            <p style={{ marginTop: 0 }}>
              {confirmTarget.role === 'owner'
                ? "This will remove the device from your account and from anyone you've shared it with. You can re-claim it later using the code from the card in your box."
                : 'This will remove your access to this device. The owner can share it with you again later.'}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button type="button" className="btn btn-danger" onClick={confirmRemove} disabled={confirmBusy} style={{ width: 'auto', flex: 1, marginTop: 0 }}>
                {confirmBusy ? 'Removing…' : (confirmTarget.role === 'owner' ? 'Remove device' : 'Remove access')}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConfirmTarget(null)} disabled={confirmBusy} style={{ width: 'auto', flex: 1, marginTop: 0 }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}